import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 * @property {HTMLAnchorElement} [tryNowLink] - Try Now CTA shown when the cart is empty.
 * @property {HTMLElement} [cartControl] - Cart trigger wrapper swapped with the Try Now link.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    if (this.dataset.tryNowEnabled === 'true') {
      this.#updateTryNowVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { itemCount?: number, source?: string } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount !== undefined) {
      this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
    }

    this.#updateTryNowVisibility();
  };

  /**
   * Toggles between the Try Now CTA and cart button based on cart item count.
   */
  #updateTryNowVisibility() {
    if (this.dataset.tryNowEnabled !== 'true') return;

    const { tryNowLink, cartControl } = this.refs;

    if (!(tryNowLink instanceof HTMLElement) || !(cartControl instanceof HTMLElement)) return;

    requestAnimationFrame(async () => {
      const itemCount = this.#resolveCartItemCount();
      const showTryNow = itemCount === 0;

      if (showTryNow) {
        await this.#closeCartDrawerIfOpen(cartControl);
      }

      tryNowLink.toggleAttribute('hidden', !showTryNow);
      cartControl.toggleAttribute('hidden', showTryNow);
    });
  }

  /**
   * Closes an open cart drawer before hiding the cart control.
   * An open modal dialog blocks page interaction even when its parent is hidden.
   * @param {HTMLElement} cartControl
   */
  async #closeCartDrawerIfOpen(cartControl) {
    const cartDrawer = cartControl.querySelector('cart-drawer-component');

    if (!(cartDrawer instanceof HTMLElement)) return;

    const dialog = cartDrawer.querySelector('dialog');

    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    if ('closeDialog' in cartDrawer && typeof cartDrawer.closeDialog === 'function') {
      await /** @type {{ closeDialog: () => Promise<void> }} */ (cartDrawer).closeDialog();
      return;
    }

    if ('close' in cartDrawer && typeof cartDrawer.close === 'function') {
      /** @type {{ close: () => void }} */ (cartDrawer).close();
    }

    this.#resetBodyScrollLock();
    dialog.close();
  }

  /**
   * Clears scroll lock styles if a dialog was closed without running closeDialog.
   */
  #resetBodyScrollLock() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Resolves the current cart item count from the header cart icon.
   * @returns {number}
   */
  #resolveCartItemCount() {
    const cartIcon = this.querySelector('cart-icon');

    if (cartIcon && 'currentCartCount' in cartIcon) {
      return /** @type {{ currentCartCount: number }} */ (cartIcon).currentCartCount;
    }

    const countElement = this.querySelector('[ref="cartBubbleCount"]');
    return parseInt(countElement?.textContent ?? '0', 10);
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
